import { useState } from "react";

import { Drawer } from "@components/Drawer";

import { Button, Label } from "@components";

import styles from "@styles/forms-flow/imported-audience.module.scss";

import { BoxTitles } from "@boxes/BoxTitles";
import { Case, If, Switch } from "@components/control-statements";

import { Environment } from "@core/Environment";
import { FlowAPI } from "@providers/FlowAPI";

import file from "assets/csv/OLGA-EXEMPLO-PUBLICO.csv"

import classNames from "classnames/bind";
const css = classNames.bind(styles);

const statusName = {
  NO_FILE: "NO_FILE",
  UPLOADING: "UPLOADING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

const MAX_FILE_SIZE = 5;

export const ImportedAudienceForm = ({
  visible,
  onClose,
  node,
  onChange,
  onSave,
}) => {
  const [status, setStatus] = useState(
    node.data.fileName ? statusName.SUCCESS : statusName.NO_FILE
  );

  const onChangeNode = (changedNode) => {
    onChange({
      ...node,
      data: {
        ...(node.data || {}),
        ...changedNode,
      },
    });
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];

    if (file) {
      const fileSize = file.size / 1024 / 1024;
      const fileType = file.type;
      const fileName = file.name;

      if (fileSize > MAX_FILE_SIZE) {
        alert({ text: `Escolha um arquivo com no máximo ${MAX_FILE_SIZE}MB` });
        return false;
      }
      const pattern = /text\/csv|application\/vnd\.ms-excel/;

      if (!fileType.match(pattern)) {
        alert({
          text: `Arquivo inválido: ${fileName}. Tipo: "${fileType}" não permitido!`,
        });
        return false;
      }

      const reader = new FileReader();
      setStatus(statusName.UPLOADING);

      reader.onload = async (readerEvent) => {
        const response = await FlowAPI.putAudienceResource({
          environmentId: Environment.id,
          mimeType: fileType,
          file: readerEvent.target.result.split("base64,")[1],
        });

        const responseData = response.getData({ fileName: null });
        const fileName = responseData?.fileName;
        if (fileName) {
          setStatus(statusName.SUCCESS);
          onChangeNode({ fileName });
        } else {
          setStatus(statusName.ERROR);
        }
      };

      reader.readAsDataURL(file);
    }
  };

  const clearFile = () => {
    onChangeNode({ fileName: null });
    onSave(
      {
        ...node,
        data: {
          ...(node.data || {}),
          fileName: null,
        },
      },
      false
    );
    setStatus(statusName.NO_FILE);
  };

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        setStatus(statusName.NO_FILE);
        onClose();
      }}
      footer={
        <Button
          className="w-100"
          design="primary"
          disabled={!node?.data?.fileName}
          onClick={() => {
            onSave(node);
            onClose();
          }}
        >
          Salvar público importado
        </Button>
      }
      title={BoxTitles.ImportedAudience}
    >
      <div className={styles.message}>
        Lembre-se de carregar um arquivo que esteja dentro do nosso padrão. Para
        verificar um modelo, basta baixá-lo{" "}
        <a href={file} download="OLGA-EXEMPLO-PUBLICO.csv">
          clicando aqui
        </a>
      </div>

      <Label>Realize o upload do seu arquivo:</Label>
      <label
        for="upload"
        className={css({
          uploadFile: true,
          uploaded: status === statusName.SUCCESS,
        })}
      >
        <input
          type="file"
          id="upload"
          accept=".csv"
          onChange={(e) => uploadFile(e)}
        />

        <If cond={status !== statusName.UPLOADING}>
          <div
            className={css({
              icon: true,
              fileUploaded: status === statusName.SUCCESS,
              fileUploadedError: status === statusName.ERROR,
            })}
          >
            <Switch value={status}>
              <Case value={statusName.ERROR}>
                <i className="far fa-xmark" />
              </Case>
              <Case value={statusName.SUCCESS}>
                <i className="far fa-file-check" />
              </Case>
              <Case value={statusName.NO_FILE}>
                <i className="fal fa-file-arrow-down" />
              </Case>
            </Switch>
          </div>
        </If>

        <If cond={status === statusName.UPLOADING}>
          <div className={styles.loading}>
            <span className={styles.loader}></span>
          </div>
        </If>

        <If cond={status === statusName.SUCCESS}>
          <span className={styles.fileStatusText}>
            Arquivo carregado com sucesso
          </span>
        </If>

        <If cond={status === statusName.ERROR}>
          <span className={styles.text}>
            Houve uma falha no carregamento do arquivo
          </span>
        </If>

        <If cond={status !== statusName.UPLOADING}>
          <div className="d-flex align-center">
            <span
              className={css({
                clickHere: true,
                uploaded: status === statusName.SUCCESS,
              })}
            >
              Clique aqui
            </span>
            <If cond={status === statusName.NO_FILE}>
              <span className={styles.text}>para fazer upload de arquivos</span>
            </If>
            <If cond={status === statusName.SUCCESS}>
              <span className={styles.text}>para alterar o arquivo</span>
            </If>
            <If cond={status === statusName.ERROR}>
              <span className={styles.text}>para tentar novamente</span>
            </If>
          </div>

          <If cond={status === statusName.NO_FILE}>
            <span className={styles.maxSize}>
              Máximo de <span className={styles.size}>{MAX_FILE_SIZE}</span> MB
            </span>
          </If>
        </If>
      </label>

      <If cond={node?.data?.fileName}>
        <div className={styles.fileUploadedBox}>
          <div className={styles.icon}>
            <i className="fal fa-file" />
          </div>
          <p>Arquivo CSV</p>
          <div
            className={styles.remove}
            onClick={async () => {
              const confirmed = await window.confirm({
                text: "Você deseja remover o arquivo de público? Esta ação não poderá ser desfeita.",
                confirmText: "Remover arquivo",
                cancelText: "Cancelar",
              });
              if (!confirmed) {
                return null;
              }
              clearFile();
            }}
          >
            <i className="far fa-times" />
          </div>
        </div>
      </If>
    </Drawer>
  );
};
